/**
 * Builds and shares a dine-in invite link — a plain deep link back to this
 * exact table with the session code baked into ?code=, so whoever opens it
 * skips DineInCodeEntry entirely. See DineIn.tsx's runOpenTable, which reads
 * this same query param and prefers it over whatever's cached in local
 * storage for the table, since a just-tapped invite link is a fresher signal
 * than a possibly-stale cached code.
 */

/** Current /dine-in/:businessId/:tableNumber URL with ?code=<sessionCode> —
 *  any other query params (e.g. a leftover dev ?mock=1) are dropped. */
export function buildDineInInviteLink(sessionCode: string): string {
  const url = new URL(window.location.href);
  url.search = "";
  url.searchParams.set("code", sessionCode);
  return url.toString();
}

export type DineInShareResult = "shared" | "copied" | "dismissed" | "error";

/**
 * Native share sheet when available (mobile — WhatsApp, SMS, etc.), plain
 * clipboard copy otherwise. Mirrors PendingBusinessCard.tsx's handleShare: a
 * caught navigator.share() rejection just means the user closed the share
 * sheet, not a real failure, so callers shouldn't toast an error for it —
 * only "error" (clipboard also failed) warrants one.
 */
export async function shareDineInInviteLink(
  sessionCode: string,
  businessName: string
): Promise<DineInShareResult> {
  const link = buildDineInInviteLink(sessionCode);

  if (navigator.share) {
    try {
      await navigator.share({ title: `Join us at ${businessName} on NineFinds`, url: link });
      return "shared";
    } catch {
      return "dismissed";
    }
  }

  try {
    await navigator.clipboard.writeText(link);
    return "copied";
  } catch {
    return "error";
  }
}

export default { buildDineInInviteLink, shareDineInInviteLink };
